// Gateway Tools admin panel (fn-62.5 / R8).
//
// A LIVE, on-demand view of the MCP tools each AgentCore gateway *target*
// exposes — the gateway's **registered** (unfiltered, not per-agent) tool
// catalog, fetched fresh on every request via `trpc.gatewayTools.list`
// (CP `GET /admin/gateway/tools` → live AWS SDK call; nothing cached or
// persisted). This is DISTINCT from the config-repo `mcp-servers` page
// (a Postgres scan of declared MCP servers) — a different concept (live
// gateway targets vs declared config-repo servers), so it gets its own
// page + nav entry to avoid operator confusion.
//
// Admin list pages bypass the `@sb-ai-platform/ui-react` adapter seam and
// call tRPC directly (the adapter is only for the chat/inbox surfaces) —
// this page mirrors the SHAPE of `mcp-servers/list.tsx`, not its data.
//
// **Wire shape** (`GatewayToolsResponse` from `@sb-ai-platform/schemas`,
// snake_case): `{ gateway_configured, partial, targets: [...] }` where each
// target is `{ ok:true, target:{id,name,type,status}, tools:[{id,name,
// description,input_schema,read_write}], tool_source } | { ok:false,
// target, error:{code} }`. The wire `tool.id` is the namespaced
// `<target>___<tool>`; `tool.name` is already the bare name — we display
// `tool.name` and NEVER the raw `___` id.

import type { BffRouter } from '@sb-ai-platform/bff';
import type { inferRouterOutputs } from '@trpc/server';

import { trpc } from '../../trpc-client.js';

// The gateway-tools wire shape is owned by `@sb-ai-platform/schemas`
// (`GatewayToolsResponse`) and surfaced through the BFF's
// `gatewayTools.list` procedure. We derive it from the router output
// rather than re-declaring it or adding a direct `schemas` dependency on
// this SPA, so a contract change is a compile error here instead of a
// silent runtime drift behind a cast (mirrors `agents/list.tsx`).
export type GatewayToolsResponse = inferRouterOutputs<BffRouter>['gatewayTools']['list'];
export type GatewayTargetResult = GatewayToolsResponse['targets'][number];
export type GatewayTool = Extract<GatewayTargetResult, { ok: true }>['tools'][number];
export type GatewayToolReadWrite = GatewayTool['read_write'];

// ───────────────────────── Pure helpers (unit-tested) ─────────────────────

/**
 * Split a namespaced gateway tool id (`<target>___<tool>`) into its bare
 * tool name. The wire payload already carries a bare `tool.name`, so the
 * UI prefers that — this is the defensive fallback for a raw id (and is
 * unit-tested so the `___` convention is pinned). Returns the input
 * unchanged when there is no `___` separator.
 */
export function bareToolName(id: string): string {
  const sep = id.lastIndexOf('___');
  if (sep < 0) return id;
  return id.slice(sep + 3);
}

/**
 * Human label for a read/write hint. `unknown` is rendered plainly (never
 * guessed from naming) — the registered schema simply carried no hint.
 */
export function formatReadWrite(readWrite: GatewayToolReadWrite): string {
  switch (readWrite) {
    case 'read':
      return 'read';
    case 'write':
      return 'write';
    case 'unknown':
      return 'unknown';
  }
}

/** The neutral chip class for a read/write badge. We use only neutral chips
 *  for informational badges — ember/danger is reserved for genuine
 *  degraded/error signals (the partial banner + per-target error chip). */
export function readWriteChipClass(readWrite: GatewayToolReadWrite): string {
  return `sb-chip gateway-tool-rw gateway-tool-rw-${readWrite}`;
}

/** Lower-cased, class-safe target-status suffix for the status chip. A null
 *  status renders as `unknown`. */
export function targetStatusLabel(status: string | null): string {
  if (status === null || status.length === 0) return 'unknown';
  return status;
}

/** Count of failed (`ok:false`) targets — drives the degraded banner copy. */
export function countFailedTargets(targets: readonly GatewayTargetResult[]): number {
  return targets.reduce((acc, t) => acc + (t.ok ? 0 : 1), 0);
}

/**
 * Pretty-print a tool's registered `input_schema` for the collapsed disclosure,
 * or `null` when there is nothing useful to show (absent, or an empty object).
 * The schema is the registered AgentCore `SchemaDefinition` (JSON), surfaced
 * verbatim — the operator-facing "input schema" the spec's view promises.
 */
export function formatInputSchema(schema: unknown): string | null {
  if (schema === null || schema === undefined) return null;
  if (typeof schema === 'object' && Object.keys(schema as object).length === 0) return null;
  try {
    return JSON.stringify(schema, null, 2);
  } catch {
    // A non-serializable value (e.g. a cycle) is not worth surfacing raw.
    return null;
  }
}

// ───────────────────────── Presentational (pure, prop-driven) ─────────────

/**
 * The pure presentational body — takes the already-fetched
 * `GatewayToolsResponse` plus loading/error flags and a refresh callback.
 * Split out from the data-fetching wrapper so it is unit-testable without
 * mounting the tRPC tree (mirrors the codebase convention of pure render +
 * pure helpers).
 *
 * State precedence (R8):
 *   1. loading → busy notice.
 *   2. query error → alert.
 *   3. `gateway_configured:false` → "No gateway configured for this stage".
 *   4. configured + zero targets → "No targets attached to the gateway"
 *      (DISTINCT copy — zero targets is a NORMAL base-posture dev state,
 *      not an error).
 *   5. targets → grouped list; `partial:true` shows a top degraded banner;
 *      per-target `ok:false` renders an inline error chip while siblings
 *      still render.
 */
export function GatewayToolsView({
  data,
  isPending,
  errorMessage,
  isFetching,
  onRefresh,
}: {
  readonly data: GatewayToolsResponse | undefined;
  readonly isPending: boolean;
  readonly errorMessage: string | null;
  readonly isFetching: boolean;
  readonly onRefresh: () => void;
}): React.ReactElement {
  const header = (
    <div className="skills-header">
      <div>
        <h1>Gateway Tools</h1>
        <p className="skills-header-meta">
          Live, unfiltered registered tool catalog per gateway target.
        </p>
      </div>
      <button
        type="button"
        className="skills-scan-button"
        disabled={isFetching}
        onClick={onRefresh}
      >
        {isFetching ? 'Refreshing…' : 'Refresh'}
      </button>
    </div>
  );

  if (isPending) {
    return (
      <section className="page-gateway-tools" aria-busy="true">
        {header}
        <p className="skills-empty">Loading gateway tools…</p>
      </section>
    );
  }

  // Only block the whole page on error when there is NO cached catalog to show
  // (the first load failed). Once `data` exists, a failed refetch must NOT wipe
  // the already-rendered list — surface it as a non-blocking banner instead.
  if (data === undefined) {
    return (
      <section className="page-gateway-tools">
        {header}
        {errorMessage !== null ? (
          <p className="skills-error" role="alert">
            Failed to load gateway tools: {errorMessage}
          </p>
        ) : (
          <p className="skills-empty">No gateway tools.</p>
        )}
      </section>
    );
  }

  // A refresh failure over an already-loaded catalog: keep the list, warn inline.
  const refreshBanner =
    errorMessage !== null ? (
      <p className="skills-error" role="alert">
        Refresh failed (showing the last loaded catalog): {errorMessage}
      </p>
    ) : null;

  if (!data.gateway_configured) {
    return (
      <section className="page-gateway-tools">
        {header}
        {refreshBanner}
        <p className="skills-empty">No gateway configured for this stage.</p>
      </section>
    );
  }

  if (data.targets.length === 0) {
    return (
      <section className="page-gateway-tools">
        {header}
        {refreshBanner}
        <p className="skills-empty">No targets attached to the gateway.</p>
      </section>
    );
  }

  const failedCount = countFailedTargets(data.targets);

  return (
    <section className="page-gateway-tools">
      {header}

      {refreshBanner}

      {data.partial && (
        <p className="skills-error" role="alert">
          {failedCount} of {data.targets.length} targets failed to load — live view.
        </p>
      )}

      <ul className="skills-list" aria-label="Gateway targets">
        {data.targets.map((entry) => (
          <GatewayTargetCard key={entry.target.id} entry={entry} />
        ))}
      </ul>
    </section>
  );
}

/** A single gateway target: name + type + status chip, then either its
 *  tool list or an inline error chip (siblings still render). */
export function GatewayTargetCard({
  entry,
}: {
  readonly entry: GatewayTargetResult;
}): React.ReactElement {
  const { target } = entry;
  return (
    <li className="skill-card">
      <div className="skill-card-header">
        <span className="skill-card-name">{target.name}</span>
        <span className="sb-chip gateway-target-status">{targetStatusLabel(target.status)}</span>
      </div>

      <div className="skill-card-meta">
        <span className="skill-meta-item">
          <span className="skill-meta-label">Type</span>
          <span className="skill-meta-value is-plain">{target.type ?? 'unknown'}</span>
        </span>
        <span className="skill-meta-item">
          <span className="skill-meta-label">Id</span>
          <span className="skill-meta-value is-plain">
            <code>{target.id}</code>
          </span>
        </span>
      </div>

      {!entry.ok ? (
        <div className="skill-card-description">
          <span className="skills-row-error" role="alert">
            failed to load — {entry.error.code}
          </span>
        </div>
      ) : entry.tools.length === 0 ? (
        <div className="skill-card-description">
          <span className="skill-meta-value is-plain">
            {entry.tool_source === 'unavailable'
              ? 'No registered tool catalog for this target type.'
              : 'No tools registered.'}
          </span>
        </div>
      ) : (
        <ul className="gateway-target-tools" aria-label={`Tools for ${target.name}`}>
          {entry.tools.map((tool) => (
            <li key={tool.id} className="gateway-tool-row">
              <div className="gateway-tool-row-head">
                <span className="gateway-tool-name">{tool.name}</span>
                <span className={readWriteChipClass(tool.read_write)}>
                  {formatReadWrite(tool.read_write)}
                </span>
              </div>
              {tool.description !== null && tool.description.length > 0 && (
                <p className="gateway-tool-description">{tool.description}</p>
              )}
              {formatInputSchema(tool.input_schema) !== null && (
                <details className="gateway-tool-schema">
                  <summary>Input schema</summary>
                  <pre className="gateway-tool-schema-body">
                    {formatInputSchema(tool.input_schema)}
                  </pre>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

// ───────────────────────── Data-fetching wrapper ──────────────────────────

/**
 * The route component. Calls `trpc.gatewayTools.list` directly (no input;
 * the CP endpoint takes none for v1) and feeds the result into the pure
 * `GatewayToolsView`. `refetch()` drives the manual Refresh button.
 */
export function GatewayToolsListPage(): React.ReactElement {
  const query = trpc.gatewayTools.list.useQuery(undefined, {
    // Live, on-demand: don't silently serve a stale catalog.
    staleTime: 0,
  });

  return (
    <GatewayToolsView
      data={query.data}
      isPending={query.isPending}
      errorMessage={query.error !== null ? query.error.message : null}
      isFetching={query.isFetching}
      onRefresh={() => {
        void query.refetch();
      }}
    />
  );
}
